import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowRight, Scale, DollarSign, Zap, BookOpen } from "lucide-react";
import { Link } from "react-router-dom";
import medicalWeightLossHero from "@/assets/medical-weight-loss-hero.png";
import orderlyMedsLogo from "@/assets/orderly-meds-logo.png";

interface MedicalWeightLossProps {
  onOpenModal?: () => void;
  orderlyLink?: string | null;
}

const MedicalWeightLoss = ({ onOpenModal, orderlyLink }: MedicalWeightLossProps) => {
  const [pricingOpen, setPricingOpen] = useState(false);
  const [eligibilityOpen, setEligibilityOpen] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const imageRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Use custom orderly link if provided, otherwise use default
  const defaultOrderlyLink = "https://patients.orderlymeds.com/eligibility?orid=466428";
  const effectiveOrderlyLink = orderlyLink || defaultOrderlyLink;

  // Lazy load image
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setImageLoaded(true);
            observer.disconnect();
          }
        });
      },
      { rootMargin: '200px 0px', threshold: 0.01 }
    );

    if (imageRef.current) {
      observer.observe(imageRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const features = [
    "Semaglutide",
    "Tirzepatide", 
    "Retatrutide",
    "Monthly Subscriptions",
  ];

  const benefits = [
    "Licensed providers via telehealth",
    "No insurance required",
    "Clear monthly pricing",
    "Ongoing support included",
    "Home delivery",
  ];

  return (
    <>
      <section id="weight" className="py-12 md:py-28 scroll-mt-20 border-b border-white/10 bg-gradient-to-br from-[#0D1B2A] via-[#1B263B] to-[#0D1B2A] overflow-hidden">
        <div className="container mx-auto px-0 md:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col lg:grid lg:grid-cols-2 gap-0 lg:gap-20 items-center">
              
              {/* Mobile: Headline & Description first */}
              <div className="space-y-4 lg:hidden px-6">
                {/* Badge */}
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#ffdb4d]/10 border border-[#ffdb4d]/20">
                  <Scale className="w-5 h-5 text-[#ffdb4d]" />
                  <span className="text-sm font-bold text-[#ffdb4d]">GLP-1 Weight Loss</span>
                </div>

                <div className="space-y-3">
                  <h2 className="text-3xl sm:text-4xl font-black text-white leading-tight">
                    Medical Weight Loss,{" "}
                    <span className="text-[#ffdb4d]">Without the Runaround</span>
                  </h2>
                  <p className="text-base text-white/70 leading-relaxed">
                    Access GLP-1 therapies through licensed providers via telehealth. No insurance required. Clear monthly pricing. Ongoing support.
                  </p>
                </div>
              </div>

              {/* Mobile: Visual Card second | Desktop: on right */}
              <div 
                ref={imageRef}
                className="relative group cursor-pointer lg:order-2 w-full lg:w-auto mt-6 lg:mt-0"
              >
                <div className="aspect-[4/5] md:aspect-square rounded-none lg:rounded-3xl overflow-hidden relative shadow-none lg:shadow-xl hover:shadow-2xl transition-shadow duration-500 bg-[#1B263B]">
                  {/* Background image - lazy loaded */}
                  <div 
                    className={`parallax-bg absolute inset-0 bg-cover bg-center lg:inset-[-15%] lg:transition-transform lg:duration-300 lg:ease-out lg:group-hover:scale-110 transition-opacity duration-500 ${
                      imageLoaded ? 'opacity-100' : 'opacity-0'
                    }`}
                    style={{
                      backgroundImage: imageLoaded ? `url(${medicalWeightLossHero})` : 'none',
                      backgroundPosition: 'center top',
                      transform: 'translateZ(0)',
                    }}
                  />
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10 transition-opacity duration-500 group-hover:from-black/70" />
                  {/* Glow effect on hover */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-[#ffdb4d]/5" />
                  
                  {/* Content positioned at bottom */}
                  <div className="absolute inset-0 flex flex-col justify-end p-8">
                    <div className="text-center space-y-3">
                      {/* Partner Badge */}
                      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/90 shadow-lg">
                        <span className="text-[#0D1B2A]/70 text-xs font-medium">powered by</span>
                        <img src={orderlyMedsLogo} alt="OrderlyMeds" className="h-4" />
                      </div>
                      {/* Icon */}
                      <Scale className="w-10 h-10 mx-auto text-white/80 group-hover:text-[#ffdb4d] transition-colors duration-500 drop-shadow-lg" />
                      {/* Title */}
                      <div className="text-2xl md:text-3xl font-black text-white group-hover:text-[#ffdb4d] transition-colors duration-500 drop-shadow-lg">
                        GLP-1 Therapy
                      </div>
                      {/* Price Display */}
                      <div className="space-y-1">
                        <p className="text-white/80 text-sm font-medium">Semaglutide & Tirzepatide</p>
                        <span className="text-2xl md:text-3xl font-black text-[#ffdb4d] drop-shadow-md">Starting at $74/mo</span>
                      </div>
                      {/* Animated underline */}
                      <div className="w-0 group-hover:w-24 h-0.5 bg-[#ffdb4d] mx-auto transition-all duration-500 ease-out" />
                    </div>
                  </div>
                </div>
                {/* Decorative elements - hidden on mobile */}
                <div className="hidden lg:block absolute -top-4 -right-4 w-24 h-24 bg-wellness/10 group-hover:bg-[#ffdb4d]/20 rounded-full blur-2xl transition-colors duration-500" />
                <div className="hidden lg:block absolute -bottom-8 -left-8 w-32 h-32 bg-wellness/5 group-hover:bg-[#ffdb4d]/10 rounded-full blur-3xl transition-colors duration-500" />
              </div>

              {/* Mobile: Features, Benefits, Button third | Desktop: Full content first */}
              <div className="space-y-6 lg:space-y-8 lg:order-1 px-6 lg:px-0 pt-8 lg:pt-0">
                {/* Desktop only: Badge, Headline, Description */}
                <div className="hidden lg:block space-y-6">
                  {/* Badge */}
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#ffdb4d]/10 border border-[#ffdb4d]/20">
                    <Scale className="w-5 h-5 text-[#ffdb4d]" />
                    <span className="text-sm font-bold text-[#ffdb4d]">GLP-1 Weight Loss</span>
                  </div>

                  <div className="space-y-6">
                    <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white leading-tight">
                      Medical Weight Loss,{" "}
                      <span className="text-[#ffdb4d]">Without the Runaround</span>
                    </h2>
                    <p className="text-lg md:text-xl text-white/70 leading-relaxed">
                      Access GLP-1 therapies through licensed providers via telehealth. No insurance required. Clear monthly pricing. Ongoing support.
                    </p>
                  </div>
                </div>

                {/* Medications/Features */}
                <div className="space-y-3">
                  <h4 className="text-sm font-bold text-white/60 uppercase tracking-wide">Available Medications</h4>
                  <div className="flex flex-wrap gap-2">
                    {features.map((feature) => (
                      <span 
                        key={feature}
                        className="px-4 py-2 bg-[#ffdb4d]/10 text-[#ffdb4d] rounded-full text-sm font-semibold transition-all duration-300 hover:scale-105 hover:bg-[#ffdb4d]/20 hover:shadow-md"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Benefits */}
                <ul className="space-y-3">
                  {benefits.map((benefit, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#ffdb4d] flex-shrink-0" />
                      <span className="text-white font-medium">{benefit}</span>
                    </li>
                  ))}
                </ul>



                {/* Price & CTA */}
                <div className="space-y-4">
                  {/* Pricing Display */}
                  <div className="space-y-2">
                    <span className="text-3xl md:text-4xl font-black text-[#ffdb4d]">Starting at $74/mo</span>
                    <div className="flex items-center gap-3 text-sm">
                      <span className="text-white/60">Cancel anytime</span>
                      <span className="text-white/50">•</span>
                      <span className="text-white/60">No hidden fees</span>
                    </div>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-3">
                    {orderlyLink ? (
                      <a 
                        href={effectiveOrderlyLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center text-lg px-8 py-6 h-auto font-bold group bg-[#ffdb4d] hover:bg-[#ffdb4d]/90 text-[#0D1B2A] w-full sm:w-auto rounded-md"
                      >
                        <Zap className="mr-2 h-5 w-5" />
                        See if You Qualify
                        <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                      </a>
                    ) : (
                      <Button 
                        size="lg" 
                        className="text-lg px-8 py-6 h-auto font-bold group bg-[#ffdb4d] hover:bg-[#ffdb4d]/90 text-[#0D1B2A] w-full sm:w-auto"
                        onClick={onOpenModal}
                      >
                        <Zap className="mr-2 h-5 w-5" />
                        See if You Qualify
                        <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    )}
                    {orderlyLink ? (
                      <a 
                        href={effectiveOrderlyLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-lg bg-white/10 hover:bg-white/20 text-white text-lg font-semibold transition-all duration-300 hover:scale-105 w-full sm:w-auto"
                      >
                        <BookOpen className="w-5 h-5" />
                        Learn More
                      </a>
                    ) : (
                      <Link 
                        to="/wellness-blog/glp1-weight-loss"
                        className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-lg bg-white/10 hover:bg-white/20 text-white text-lg font-semibold transition-all duration-300 hover:scale-105 w-full sm:w-auto"
                      >
                        <BookOpen className="w-5 h-5" />
                        Learn More
                      </Link>
                    )}
                  </div>
                </div>
                
                {/* Compliance Disclaimer */}
                <p className="text-xs text-white/50 mt-4 leading-relaxed">
                  *Results vary by individual. GLP-1 medications are FDA-approved prescription drugs 
                  prescribed by licensed providers based on individual assessment. Individual results may vary.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Eligibility Modal */}
      <Dialog open={eligibilityOpen} onOpenChange={setEligibilityOpen}>
        <DialogContent className="max-w-5xl h-[90vh] p-0 overflow-hidden">
          <DialogHeader className="p-6 pb-0">
            <DialogTitle className="text-2xl font-bold">Check Your Eligibility</DialogTitle>
          </DialogHeader>
          <div className="flex-1 h-full min-h-0">
            <iframe
              src="https://patients.orderlymeds.com/eligibility?orid=466428"
              width="100%"
              height="100%"
              frameBorder="0"
              className="min-h-[calc(90vh-80px)]"
              title="GLP-1 Eligibility Check"
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Pricing Modal */}
      <Dialog open={pricingOpen} onOpenChange={setPricingOpen}>
        <DialogContent className="max-w-5xl h-[90vh] p-0 overflow-hidden">
          <DialogHeader className="p-6 pb-0">
            <DialogTitle className="text-2xl font-bold">GLP-1 Pricing and Plans</DialogTitle>
          </DialogHeader>
          <div className="flex-1 h-full min-h-0">
            <iframe
              src="https://orderlymeds.com/simplestart/?orid=466428"
              width="100%"
              height="100%"
              frameBorder="0"
              className="min-h-[calc(90vh-80px)]"
              title="GLP-1 Pricing and Plans"
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MedicalWeightLoss;